import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  CanMatchFn,
  Route,
  Router,
  Routes,
  UrlSegment,
} from '@angular/router';
import { inject } from '@angular/core';

import { authGuard } from './core/auth/auth.guard';
import { roleGuard } from './core/auth/role.guard';
import { twoFactorSetupGuard } from './core/auth/two-factor-setup.guard';
import { ShellLayoutComponent } from './core/layout/shell-layout.component';

import { LoginPageComponent } from './features/auth/pages/login-page.component';
import { RegisterPageComponent } from './features/auth/pages/register-page.component';
import { RegisterVerifyPageComponent } from './features/auth/pages/register-verify-page.component';
import { ClientInviteAcceptPageComponent } from './features/auth/pages/client-invite-accept-page.component';
import { StaffInviteAcceptPageComponent } from './features/auth/pages/staff-invite-accept-page.component';

import { AppointmentsListPageComponent } from './features/appointments/pages/appointments-list-page.component';
import { AppointmentCreatePageComponent } from './features/appointments/pages/appointment-create-page.component';
import { AppointmentClientIntentMapPageComponent } from './features/appointments/pages/appointment-client-intent-map-page.component';
import { AppointmentCapacityMatrixPageComponent } from './features/appointments/pages/appointment-capacity-matrix-page.component';
import { AppointmentCapacityStagePageComponent } from './features/appointments/pages/appointment-capacity-stage-page.component';
import { AppointmentFluidCapacityPrototypePageComponent } from './features/appointments/pages/appointment-fluid-capacity-prototype-page.component';
import { AppointmentEditPageComponent } from './features/appointments/pages/appointment-edit-page.component';
import { AppointmentsTabsPageComponent } from './features/appointments/pages/appointments-tabs-page.component';
import { AppointmentChangeRequestsPageComponent } from './features/appointments/pages/appointment-change-requests-page.component';

import { DowntimeListPageComponent } from './features/downtime/pages/downtime-list-page.component';
import { DowntimeCreatePageComponent } from './features/downtime/pages/downtime-create-page.component';
import { DowntimeDefaultRedirectComponent, DowntimeTabsPageComponent } from './features/downtime/pages/downtime-tabs-page.component';
import { SpecialistDowntimeListPageComponent } from './features/specialist-downtime/pages/specialist-downtime-list-page.component';
import { SpecialistDowntimeCreatePageComponent } from './features/specialist-downtime/pages/specialist-downtime-create-page.component';

import { RescheduleProposalsPageComponent } from './features/reschedule-proposals/pages/reschedule-proposals-page.component';

import { NotificationQueuePageComponent } from './features/notification-queue/pages/notification-queue-page.component';
import { NotificationTemplatesPageComponent } from './features/notification-templates/pages/notification-templates-page.component';
import { NotificationsTabsPageComponent } from './features/notifications/pages/notifications-tabs-page.component';
import { SlotsPageComponent } from './features/slots/pages/slots-page.component';
import { UsersPageComponent } from './features/users/pages/users-page.component';
import { UserProfilePageComponent } from './features/users/pages/user-profile-page.component';
import { ProcedureTypesPageComponent } from './features/procedure-types/pages/procedure-types-page.component';
import { DevicesPageComponent } from './features/devices/pages/devices-page.component';
import { ClientProfilePageComponent } from './features/clients/pages/client-profile-page.component';
import { CompaniesPageComponent } from './features/companies/pages/companies-page.component';
import { SettingsPageComponent } from './features/settings/pages/settings-page.component';
import { DataRetentionPageComponent } from './features/data-retention/pages/data-retention-page.component';
import { SecurityPageComponent } from './features/security/pages/security-page.component';
import { TelegramConnectorPageComponent } from './features/telegram/pages/telegram-connector-page.component';

const RESERVED_ROOT_ROUTES = new Set([
  'login',
  'register',
  'appointments',
  'my-profile',
  'downtime',
  'specialist-downtime',
  'notifications',
  'slots',
  'devices',
  'users',
  'procedure-types',
  'companies',
  'settings',
  'clinics',
  'global-settings',
  'data-retention',
  'security',
  'connectors',
  'telegram',
  'tech',
]);

const tenantSlugCanMatch: CanMatchFn = (
  _route: Route,
  segments: UrlSegment[],
): boolean => {
  const slug = segments[0]?.path?.trim().toLowerCase();

  if (!slug) {
    return false;
  }

  return !RESERVED_ROOT_ROUTES.has(slug);
};

const tenantSlugRootRedirectGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
) => {
  const router = inject(Router);
  const companySlug = route.paramMap.get('companySlug')?.trim().toLowerCase();

  if (!companySlug) {
    return router.parseUrl('/login');
  }

  return router.createUrlTree(['/', companySlug, 'login']);
};

export const APP_ROUTES: Routes = [
  {
    path: ':companySlug/register/invite',
    component: ClientInviteAcceptPageComponent,
    canMatch: [tenantSlugCanMatch],
  },
  {
    path: ':companySlug/register/staff-invite',
    component: StaffInviteAcceptPageComponent,
    canMatch: [tenantSlugCanMatch],
  },
  {
    path: ':companySlug/register/verify',
    component: RegisterVerifyPageComponent,
    canMatch: [tenantSlugCanMatch],
  },
  {
    path: ':companySlug/login',
    component: LoginPageComponent,
    canMatch: [tenantSlugCanMatch],
  },
  {
    path: ':companySlug/register',
    component: RegisterPageComponent,
    canMatch: [tenantSlugCanMatch],
  },
  {
    path: ':companySlug',
    component: LoginPageComponent,
    canActivate: [tenantSlugRootRedirectGuard],
    canMatch: [tenantSlugCanMatch],
  },
  {
    path: 'login',
    component: LoginPageComponent
  },
  {
    path: ':companySlug',
    component: ShellLayoutComponent,
    canMatch: [tenantSlugCanMatch],
    canActivate: [authGuard],
    canActivateChild: [twoFactorSetupGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'appointments'
      },
      {
        path: 'my-profile',
        component: ClientProfilePageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['client']
        }
      },
      {
        path: 'appointments/new',
        component: AppointmentCreatePageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager', 'reception', 'client']
        }
      },
      {
        path: 'appointments/:id/edit',
        component: AppointmentEditPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager', 'reception', 'client']
        }
      },
      {
        path: 'appointments',
        component: AppointmentsTabsPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager', 'technician', 'specialist', 'reception', 'client']
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'schedule',
          },
          {
            path: 'schedule',
            component: AppointmentsListPageComponent,
            canActivate: [roleGuard],
            data: {
              roles: ['system_admin', 'company_admin', 'manager', 'technician', 'specialist', 'reception', 'client']
            }
          },
          {
            path: 'reschedule',
            component: RescheduleProposalsPageComponent,
            canActivate: [roleGuard],
            data: {
              roles: ['system_admin', 'company_admin', 'manager', 'reception']
            }
          },
          {
            path: 'client-map',
            component: AppointmentClientIntentMapPageComponent,
            canActivate: [roleGuard],
            data: {
              roles: ['system_admin', 'company_admin', 'manager', 'reception']
            }
          },
          {
            path: 'fluid-capacity',
            component: AppointmentFluidCapacityPrototypePageComponent,
            canActivate: [roleGuard],
            data: {
              roles: ['system_admin', 'company_admin', 'manager', 'reception']
            }
          },
          {
            path: 'capacity-stage',
            component: AppointmentCapacityStagePageComponent,
            canActivate: [roleGuard],
            data: {
              roles: ['system_admin', 'company_admin', 'manager', 'reception']
            }
          },
          {
            path: 'capacity-matrix',
            component: AppointmentCapacityMatrixPageComponent,
            canActivate: [roleGuard],
            data: {
              roles: ['system_admin', 'company_admin', 'manager', 'reception']
            }
          },
          {
            path: 'change-requests',
            component: AppointmentChangeRequestsPageComponent,
            canActivate: [roleGuard],
            data: {
              roles: ['system_admin', 'company_admin', 'manager', 'reception']
            }
          },
        ],
      },
      {
        path: 'downtime',
        component: DowntimeTabsPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager', 'technician', 'specialist', 'reception']
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            component: DowntimeDefaultRedirectComponent,
          },
          {
            path: 'device',
            component: DowntimeListPageComponent,
            canActivate: [roleGuard],
            data: {
              roles: ['system_admin', 'company_admin', 'manager', 'technician', 'reception']
            }
          },
          {
            path: 'specialist',
            component: SpecialistDowntimeListPageComponent,
            canActivate: [roleGuard],
            data: {
              roles: ['system_admin', 'company_admin', 'manager', 'specialist', 'reception']
            }
          },
        ],
      },
      {
        path: 'specialist-downtime',
        redirectTo: 'downtime/specialist',
        pathMatch: 'full',
      },
      {
        path: 'downtime/new',
        redirectTo: 'downtime/device/new',
        pathMatch: 'full',
      },
      {
        path: 'specialist-downtime/new',
        redirectTo: 'downtime/specialist/new',
        pathMatch: 'full',
      },
      {
        path: 'downtime/device/new',
        component: DowntimeCreatePageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager', 'technician']
        }
      },
      {
        path: 'downtime/specialist/new',
        component: SpecialistDowntimeCreatePageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager', 'specialist']
        }
      },
      {
        path: 'notifications',
        component: NotificationsTabsPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager']
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'queue',
          },
          {
            path: 'queue',
            component: NotificationQueuePageComponent,
            canActivate: [roleGuard],
            data: {
              roles: ['system_admin', 'company_admin', 'manager']
            }
          },
          {
            path: 'templates',
            component: NotificationTemplatesPageComponent,
            canActivate: [roleGuard],
            data: {
              roles: ['system_admin', 'company_admin', 'manager']
            }
          },
        ],
      },
      {
        path: 'slots',
        component: SlotsPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager', 'reception', 'specialist']
        }
      },
      {
        path: 'devices',
        component: DevicesPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager']
        }
      },
      {
        path: 'users',
        component: UsersPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager']
        }
      },
      {
        path: 'users/:id',
        component: UserProfilePageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager']
        }
      },
      {
        path: 'procedure-types',
        component: ProcedureTypesPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager']
        }
      },
      {
        path: 'companies',
        component: CompaniesPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin']
        }
      },
      {
        path: 'settings',
        component: SettingsPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager'],
          settingsMode: 'general',
        }
      },
      {
        path: 'clinics',
        component: SettingsPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager'],
          settingsMode: 'clinics',
        }
      },
      {
        path: 'companies/:companyId/settings',
        component: SettingsPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin'],
          settingsMode: 'general',
        }
      },
      {
        path: 'companies/:companyId/notification-templates',
        component: NotificationTemplatesPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin']
        }
      },
      {
        path: 'global-settings',
        component: SettingsPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin'],
          settingsMode: 'general',
          initialSettingsTab: 'global',
        }
      },
      {
        path: 'data-retention',
        component: DataRetentionPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin']
        }
      },
      {
        path: 'security',
        component: SecurityPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager', 'technician', 'specialist', 'reception', 'client']
        }
      },
      {
        path: 'connectors',
        component: TelegramConnectorPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager', 'technician', 'specialist', 'reception', 'client']
        }
      },
      {
        path: 'telegram',
        redirectTo: 'connectors',
        pathMatch: 'full',
      },
    ]
  },
  {
    path: '',
    component: ShellLayoutComponent,
    canActivate: [authGuard],
    canActivateChild: [twoFactorSetupGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'appointments'
      },
      {
        path: 'my-profile',
        component: ClientProfilePageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['client']
        }
      },
      {
        path: 'appointments/new',
        component: AppointmentCreatePageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager', 'reception', 'client']
        }
      },
      {
        path: 'appointments/:id/edit',
        component: AppointmentEditPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager', 'reception', 'client']
        }
      },
      {
        path: 'appointments',
        component: AppointmentsTabsPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager', 'technician', 'specialist', 'reception', 'client']
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'schedule',
          },
          {
            path: 'schedule',
            component: AppointmentsListPageComponent,
            canActivate: [roleGuard],
            data: {
              roles: ['system_admin', 'company_admin', 'manager', 'technician', 'specialist', 'reception', 'client']
            }
          },
          {
            path: 'reschedule',
            component: RescheduleProposalsPageComponent,
            canActivate: [roleGuard],
            data: {
              roles: ['system_admin', 'company_admin', 'manager', 'reception']
            }
          },
          {
            path: 'client-map',
            component: AppointmentClientIntentMapPageComponent,
            canActivate: [roleGuard],
            data: {
              roles: ['system_admin', 'company_admin', 'manager', 'reception']
            }
          },
          {
            path: 'fluid-capacity',
            component: AppointmentFluidCapacityPrototypePageComponent,
            canActivate: [roleGuard],
            data: {
              roles: ['system_admin', 'company_admin', 'manager', 'reception']
            }
          },
          {
            path: 'capacity-stage',
            component: AppointmentCapacityStagePageComponent,
            canActivate: [roleGuard],
            data: {
              roles: ['system_admin', 'company_admin', 'manager', 'reception']
            }
          },
          {
            path: 'capacity-matrix',
            component: AppointmentCapacityMatrixPageComponent,
            canActivate: [roleGuard],
            data: {
              roles: ['system_admin', 'company_admin', 'manager', 'reception']
            }
          },
          {
            path: 'change-requests',
            component: AppointmentChangeRequestsPageComponent,
            canActivate: [roleGuard],
            data: {
              roles: ['system_admin', 'company_admin', 'manager', 'reception']
            }
          },
        ],
      },
      {
        path: 'downtime',
        component: DowntimeTabsPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager', 'technician', 'specialist', 'reception']
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            component: DowntimeDefaultRedirectComponent,
          },
          {
            path: 'device',
            component: DowntimeListPageComponent,
            canActivate: [roleGuard],
            data: {
              roles: ['system_admin', 'company_admin', 'manager', 'technician', 'reception']
            }
          },
          {
            path: 'specialist',
            component: SpecialistDowntimeListPageComponent,
            canActivate: [roleGuard],
            data: {
              roles: ['system_admin', 'company_admin', 'manager', 'specialist', 'reception']
            }
          },
        ],
      },
      {
        path: 'specialist-downtime',
        redirectTo: 'downtime/specialist',
        pathMatch: 'full',
      },
      {
        path: 'downtime/new',
        redirectTo: 'downtime/device/new',
        pathMatch: 'full',
      },
      {
        path: 'specialist-downtime/new',
        redirectTo: 'downtime/specialist/new',
        pathMatch: 'full',
      },
      {
        path: 'downtime/device/new',
        component: DowntimeCreatePageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager', 'technician']
        }
      },
      {
        path: 'downtime/specialist/new',
        component: SpecialistDowntimeCreatePageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager', 'specialist']
        }
      },
      {
        path: 'notifications',
        component: NotificationsTabsPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager']
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'queue',
          },
          {
            path: 'queue',
            component: NotificationQueuePageComponent,
            canActivate: [roleGuard],
            data: {
              roles: ['system_admin', 'company_admin', 'manager']
            }
          },
          {
            path: 'templates',
            component: NotificationTemplatesPageComponent,
            canActivate: [roleGuard],
            data: {
              roles: ['system_admin', 'company_admin', 'manager']
            }
          },
        ],
      },
      {
        path: 'slots',
        component: SlotsPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager', 'reception', 'specialist']
        }
      },
      {
        path: 'devices',
        component: DevicesPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager']
        }
      },
      {
        path: 'users',
        component: UsersPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager']
        }
      },
      {
        path: 'users/:id',
        component: UserProfilePageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager']
        }
      },
      {
        path: 'procedure-types',
        component: ProcedureTypesPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager']
        }
      },
      {
        path: 'companies',
        component: CompaniesPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin']
        }
      },
      {
        path: 'settings',
        component: SettingsPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager'],
          settingsMode: 'general',
        }
      },
      {
        path: 'clinics',
        component: SettingsPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager'],
          settingsMode: 'clinics',
        }
      },
      {
        path: 'companies/:companyId/settings',
        component: SettingsPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin'],
          settingsMode: 'general',
        }
      },
      {
        path: 'companies/:companyId/notification-templates',
        component: NotificationTemplatesPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin']
        }
      },
      {
        path: 'global-settings',
        component: SettingsPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin'],
          settingsMode: 'general',
          initialSettingsTab: 'global',
        }
      },
      {
        path: 'data-retention',
        component: DataRetentionPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin']
        }
      },
      {
        path: 'security',
        component: SecurityPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager', 'technician', 'specialist', 'reception', 'client']
        }
      },
      {
        path: 'connectors',
        component: TelegramConnectorPageComponent,
        canActivate: [roleGuard],
        data: {
          roles: ['system_admin', 'company_admin', 'manager', 'technician', 'specialist', 'reception', 'client']
        }
      },
      {
        path: 'telegram',
        redirectTo: 'connectors',
        pathMatch: 'full',
      },
    ]
  },
  {
    path: '**',
    redirectTo: ''
  }
];
